import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  Shield, 
  QrCode, 
  Clock, 
  MapPin, 
  Users,
  ChevronRight,
  Smartphone,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-600 to-red-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-white bg-opacity-20 p-4 rounded-full">
                <Heart className="h-16 w-16" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Your Emergency Health Identity
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-red-100 max-w-3xl mx-auto">
              Smart QR-based medical card with real-time consent management. 
              Access critical health information when seconds matter most.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register" className="btn-primary bg-white text-red-600 hover:bg-gray-100">
                Get Started Free
              </Link>
              <Link to="/scan" className="btn-outline border-white text-white hover:bg-white hover:text-red-600">
                <QrCode className="h-5 w-5 inline mr-2" />
                Scan QR Code
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How MedCard Pro Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Simple, secure, and life-saving technology at your fingertips
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="text-center">
              <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <QrCode className="h-10 w-10 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                1. Create Your Profile
              </h3>
              <p className="text-gray-600">
                Add your medical information, emergency contacts, and generate your unique QR code
              </p>
            </div>

            {/* Feature 2 */}
            <div className="text-center">
              <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Smartphone className="h-10 w-10 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                2. Emergency Scan
              </h3>
              <p className="text-gray-600">
                First responders scan your QR code to request access to your medical information
              </p>
            </div>

            {/* Feature 3 */}
            <div className="text-center">
              <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="h-10 w-10 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                3. Real-time Consent
              </h3>
              <p className="text-gray-600">
                You receive instant notification and control access with one tap approval
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose MedCard Pro?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card-hover">
              <Clock className="h-8 w-8 text-primary mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                60-Second Auto-Approval
              </h3>
              <p className="text-gray-600">
                If you're unconscious, access is automatically granted after 60 seconds
              </p>
            </div>

            <div className="card-hover">
              <MapPin className="h-8 w-8 text-primary mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Nearby Hospitals
              </h3>
              <p className="text-gray-600">
                Instantly locate the nearest medical facilities with real-time directions
              </p>
            </div>

            <div className="card-hover">
              <Users className="h-8 w-8 text-primary mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Emergency Contacts
              </h3>
              <p className="text-gray-600">
                Automatically notify your loved ones when your MedCard is accessed
              </p>
            </div>

            <div className="card-hover">
              <AlertTriangle className="h-8 w-8 text-primary mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                AI First Aid Assistant
              </h3>
              <p className="text-gray-600">
                Get instant AI-powered guidance for emergency medical situations
              </p>
            </div>

            <div className="card-hover">
              <Shield className="h-8 w-8 text-primary mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Bank-Level Security
              </h3>
              <p className="text-gray-600">
                Your medical data is encrypted and protected with industry-leading security
              </p>
            </div>

            <div className="card-hover">
              <CheckCircle className="h-8 w-8 text-primary mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Complete Medical History
              </h3>
              <p className="text-gray-600">
                Store allergies, medications, conditions, and emergency procedures
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Secure Your Health Information?
          </h2>
          <p className="text-xl mb-8 text-red-100">
            Join thousands of users who trust MedCard Pro for their emergency medical needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="btn-primary bg-white text-red-600 hover:bg-gray-100">
              Create Free Account
            </Link>
            <Link to="/about" className="btn-outline border-white text-white hover:bg-white hover:text-red-600">
              Learn More
              <ChevronRight className="h-5 w-5 inline ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-primary mb-2">50K+</div>
              <div className="text-gray-600">Active Users</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary mb-2">100K+</div>
              <div className="text-gray-600">QR Scans</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary mb-2">500+</div>
              <div className="text-gray-600">Partner Hospitals</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary mb-2">4.9★</div>
              <div className="text-gray-600">User Rating</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
